import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/base/SEOHead';
import ScrollToTop from '@/components/base/ScrollToTop';
import ArticleCard from '@/components/base/ArticleCard';
import Breadcrumb from '@/components/base/Breadcrumb';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';

const categoryNames: Record<string, string> = {
  zonghe: '综合',
  dianying: '电影',
  dianshiju: '电视剧',
  zongyi: '综艺',
  jilupian: '纪录片',
  duanshipin: '短视频',
};

function renderMarkdown(content: string) {
  const lines = content.split('\n');
  const elements: JSX.Element[] = [];

  lines.forEach((line, i) => {
    if (line.startsWith('## ')) {
      elements.push(
        <h2 key={i} className="font-heading text-lg md:text-xl font-bold text-foreground-900 mt-8 mb-4">
          {line.replace('## ', '')}
        </h2>
      );
    } else if (line.startsWith('### ')) {
      elements.push(
        <h3 key={i} className="font-heading text-base md:text-lg font-semibold text-foreground-900 mt-6 mb-3">
          {line.replace('### ', '')}
        </h3>
      );
    } else if (line.startsWith('#### ')) {
      elements.push(
        <h4 key={i} className="font-heading text-sm md:text-base font-semibold text-foreground-800 mt-5 mb-2">
          {line.replace('#### ', '')}
        </h4>
      );
    } else if (line.startsWith('**') && line.endsWith('**')) {
      elements.push(
        <p key={i} className="font-semibold text-foreground-900 mt-4 mb-2">
          {line.replace(/\*\*/g, '')}
        </p>
      );
    } else if (line.trim() === '') {
      elements.push(<div key={i} className="h-2"></div>);
    } else {
      const processedLine = line
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
        .replace(/`(.*?)`/g, '<code class="bg-background-100 px-1 py-0.5 rounded text-sm text-primary-600">$1</code>');
      elements.push(
        <p
          key={i}
          className="text-sm md:text-base text-foreground-700 leading-relaxed"
          dangerouslySetInnerHTML={{ __html: processedLine }}
        />
      );
    }
  });

  return elements;
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);
  const relatedArticles = articles.filter((a) => a.slug !== slug).slice(0, 3);

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <SEOHead title="文章未找到 - 免费看片平台推荐" description="您访问的文章不存在或已被移除。" />
        <div className="min-h-screen">
          <Navbar />
          <div className="pt-20 text-center py-20">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-file-unknow-line text-2xl text-foreground-400"></i>
            </div>
            <h1 className="font-heading text-xl font-bold text-foreground-900 mb-2">文章未找到</h1>
            <p className="text-foreground-600 mb-6">您访问的文章不存在或已被移除</p>
            <Link
              to="/"
              className="inline-block px-6 py-2 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              返回首页
            </Link>
          </div>
          <Footer />
        </div>
      </>
    );
  }

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Article',
        headline: article.title,
        description: article.summary,
        datePublished: article.publishDate,
        author: {
          '@type': 'Organization',
          name: '免费看片推荐',
        },
        publisher: {
          '@type': 'Organization',
          name: '免费看片推荐',
        },
        inLanguage: 'zh-CN',
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://mianfeikanpian.com/' },
          { '@type': 'ListItem', position: 2, name: categoryNames[article.category] || article.category, item: `https://mianfeikanpian.com/category/${article.category}` },
          { '@type': 'ListItem', position: 3, name: article.title },
        ],
      },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={`${article.title} - 免费看片平台推荐`}
        description={article.summary}
        keywords={`免费看片,${article.tags.join(',')},免费看片平台推荐`}
        canonicalUrl={`https://mianfeikanpian.com/article/${slug}`}
        ogType="article"
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <div className="pt-16 bg-background-100 border-b border-background-200/70">
          <div className="max-w-4xl mx-auto">
            <Breadcrumb items={[
              { label: '首页', path: '/' },
              { label: categoryNames[article.category] || article.category, path: `/category/${article.category}` },
              { label: article.title },
            ]} />
          </div>
        </div>

        <article className="w-full py-10">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <header className="mb-8">
              <div className="flex items-center gap-2 mb-4 flex-wrap">
                <Link
                  to={`/category/${article.category}`}
                  className="text-xs px-3 py-1 rounded-md bg-accent-100 text-accent-700 hover:bg-accent-200 transition-colors whitespace-nowrap font-medium cursor-pointer"
                >
                  {categoryNames[article.category] || article.category}
                </Link>
                <span className="text-sm text-foreground-500">{article.publishDate}</span>
                <span className="text-sm text-foreground-500">· 阅读 {article.readTime} 分钟</span>
              </div>
              <h1 className="font-heading text-xl md:text-2xl lg:text-3xl font-bold text-foreground-900 mb-4 leading-tight">
                {article.title}
              </h1>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                {article.summary}
              </p>
            </header>

            <div className="mb-8 rounded-lg overflow-hidden">
              <img
                src={article.imageUrl}
                alt={article.title}
                className="w-full h-auto object-cover"
              />
            </div>

            <div className="prose-content">
              {renderMarkdown(article.content)}
            </div>

            <div className="mt-10 pt-6 border-t border-background-200/70">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm text-foreground-500">标签：</span>
                {article.tags.map((tag) => (
                  <span key={tag} className="text-xs px-3 py-1 rounded-full bg-background-100 text-foreground-600 whitespace-nowrap">
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </article>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="w-full py-14 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-900 mb-6">
                相关文章推荐
              </h2>
              <div className="flex flex-col gap-4">
                {relatedArticles.map((rel) => (
                  <ArticleCard key={rel.slug} {...rel} />
                ))}
              </div>
            </div>
          </section>
        )}

        <Footer />
      </div>
    </>
  );
}